import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Configure, InstantSearch, QueryRuleCustomData } from 'react-instantsearch-dom';
import { toggleBodyOverlay } from 'ck-utils';
import { trackSearchEvent } from 'ck-utils/algoliaAnalytics';
import { algoliaClient, INDEX_PRODUCTS } from 'src/lib/algolia';
import { SearchBarHeaderContainer } from './SearchBarHeader';
import { SearchBarResults } from './SearchBarResults';
import styles from './SearchBar.module.scss';

// This is needed to exclude empty search from our analytics
// https://www.algolia.com/doc/guides/getting-insights-and-analytics/search-analytics/out-of-the-box-analytics/how-to/how-to-remove-empty-search-from-analytics/
const searchClient = {
  ...algoliaClient,
  search(requests) {
    const newRequests = requests.map((request) => {
      // Test for empty string and change `analytics` request parameter to `false`
      if (!request.params.query || request.params.query.length === 0) {
        request.params.analytics = false;
      }
      return request;
    });

    return algoliaClient.search(newRequests);
  },
};

const SearchBar = ({ topBrands, noResultsCollectionHandle, noResultsCollectionSort }) => {
  const [redirectLink, setRedirectLink] = useState(null);
  const [isOnFocus, setIsOnFocus] = useState(false);

  const toggleFocus = (isFocused) => () => {
    setIsOnFocus(isFocused);
    toggleBodyOverlay(isFocused, 'search-bar');
  };

  // Handle search or redirect when user hit enter
  const onSearchEnter = (searchTerm, hasProducts) => e => {
    if (!searchTerm) return;
    e.preventDefault();
    trackSearchEvent({
      // eslint-disable-next-line no-nested-ternary
      action: redirectLink
        ? 'Redirect'
        : hasProducts
          ? 'Search Enter'
          : 'Suggested - No Results - Landing',
      searchTerm: searchTerm?.trim(),
      destinationUrl: redirectLink || (hasProducts ? '' : '/pages/no-results'),
    });
  };

  // Handle search or redirect or go to all-new-arrivals when click view all link
  const onViewAllClick = (searchTerm, hasProducts) => e => {
    if (searchTerm && (redirectLink || hasProducts)) {
      onSearchEnter(searchTerm, hasProducts)(e);
    } else { // no redirect or results
      trackSearchEvent({
        action: 'Suggested - No Results - View All',
        searchTerm,
        destinationUrl: '/collections/best-sellers'
      });
    }
  };

  return (
    <InstantSearch searchClient={searchClient} indexName={INDEX_PRODUCTS}>
      <Configure clickAnalytics />
      <SearchBarHeaderContainer onSearchEnter={onSearchEnter} toggleFocus={toggleFocus} />
      <SearchBarResults
        onViewAllClick={onViewAllClick}
        open={isOnFocus}
        topBrands={topBrands}
        noResultsCollectionHandle={noResultsCollectionHandle}
        noResultsCollectionSort={noResultsCollectionSort}
      />
      <div
        role="presentation"
        className={classnames(styles.onFocusOverlay, { [styles.isOnFocus]: isOnFocus })}
        onClick={toggleFocus(false)}
      />
      <QueryRuleCustomData
        transformItems={items => {
          const match = items.find(data => Boolean(data.redirect));
          setRedirectLink(match ? match.redirect : null);
          return [];
        }}
      >
        {() => null}
      </QueryRuleCustomData>
    </InstantSearch>
  );
};

SearchBar.propTypes = {
  topBrands: PropTypes.array.isRequired,
  noResultsCollectionHandle: PropTypes.string.isRequired,
  noResultsCollectionSort: PropTypes.string.isRequired,
};

export { SearchBar };
